import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

/**
 * Reusable Tooltip component that displays an info icon with a tooltip on click
 *
 * @param {string} content - The tooltip text to display
 * @param {string} title - Optional title/heading to display above the content
 * @param {string} icon - Icon type: "info" (default blue) or "required" (red attention icon)
 * @param {string} iconSrc - Optional custom icon source (overrides icon prop)
 * @param {string} className - Optional additional CSS classes for the container
 * @param {string} iconClassName - Optional additional CSS classes for the icon
 */
const Tooltip = ({
  content,
  title = null,
  icon = "info",
  iconSrc = null,
  className = "",
  iconClassName = "",
  children,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [isAbove, setIsAbove] = useState(false);
  const tooltipRef = useRef(null);
  const iconRef = useRef(null);
  const tooltipContentRef = useRef(null);

  // Calculate tooltip position when it's shown
  useEffect(() => {
    if (showTooltip && iconRef.current) {
      let rafId = null;
      let scrollEndTimeout = null;

      const updatePosition = (useActualDimensions = false) => {
        if (!iconRef.current) return;

        const iconRect = iconRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Get tooltip dimensions if available, otherwise use defaults
        let tooltipWidth = 250; // min-width from CSS
        let tooltipHeight = 50; // estimated initial height

        if (useActualDimensions && tooltipContentRef.current) {
          const tooltipRect = tooltipContentRef.current.getBoundingClientRect();
          tooltipWidth = tooltipRect.width || 250;
          tooltipHeight = tooltipRect.height || 50;
        }

        // Calculate horizontal position (centered below icon)
        const iconCenterX = iconRect.left + iconRect.width / 2;
        let left = iconCenterX - tooltipWidth / 2;

        // Adjust if tooltip would go off-screen
        if (left < 8) {
          left = 8;
        }
        if (left + tooltipWidth > viewportWidth - 8) {
          left = viewportWidth - tooltipWidth - 8;
        }

        // Calculate vertical position (below icon with margin)
        let top = iconRect.bottom + 4;
        let tooltipIsAbove = false;

        // If tooltip would go off-screen at bottom, show it above the icon
        if (top + tooltipHeight > viewportHeight - 8) {
          top = iconRect.top - tooltipHeight - 4;
          tooltipIsAbove = true;
        }

        setTooltipPosition({ top, left });
        setIsAbove(tooltipIsAbove);
      };

      // Scroll handler - only update after scroll stops
      const handleScroll = () => {
        // Cancel any pending scroll-end timeout
        if (scrollEndTimeout) {
          clearTimeout(scrollEndTimeout);
        }

        // Cancel any pending RAF
        if (rafId) {
          cancelAnimationFrame(rafId);
        }

        // Don't update during scroll - only after scrolling stops
        // This prevents jank and keeps the tooltip stable during scroll
        scrollEndTimeout = setTimeout(() => {
          updatePosition(true);
        }, 100);
      };

      // Calculate initial position immediately based on icon position
      // This prevents the flash at (0,0)
      if (iconRef.current) {
        const iconRect = iconRef.current.getBoundingClientRect();
        const iconCenterX = iconRect.left + iconRect.width / 2;
        const estimatedWidth = 250;
        const estimatedHeight = 50;

        let left = iconCenterX - estimatedWidth / 2;
        if (left < 8) left = 8;
        if (left + estimatedWidth > window.innerWidth - 8) {
          left = window.innerWidth - estimatedWidth - 8;
        }

        let top = iconRect.bottom + 4;
        let tooltipIsAbove = false;
        if (top + estimatedHeight > window.innerHeight - 8) {
          top = iconRect.top - estimatedHeight - 4;
          tooltipIsAbove = true;
        }

        setTooltipPosition({ top, left });
        setIsAbove(tooltipIsAbove);
      }

      // Then refine position after tooltip is rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updatePosition(true);
        });
      });

      const handleResize = () => {
        updatePosition(true);
      };

      // Update on scroll/resize
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        if (scrollEndTimeout) {
          clearTimeout(scrollEndTimeout);
        }
      };
    } else {
      // Reset position when hidden
      setTooltipPosition(null);
      setIsAbove(false);
    }
  }, [showTooltip]);

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        tooltipContentRef.current &&
        !tooltipContentRef.current.contains(event.target)
      ) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTooltip]);

  if (!content) {
    return null;
  }

  // Determine icon source based on icon prop or custom iconSrc
  const getIconSrc = () => {
    if (iconSrc) return iconSrc;
    if (icon === "required")
      return `${process.env.PUBLIC_URL}/images/icons/help_red.svg`;
    return `${process.env.PUBLIC_URL}/images/icons/info_blue.svg`;
  };
  const resolvedIconSrc = getIconSrc();
  const portalElement = document.body;

  const tooltipContent =
    showTooltip && tooltipPosition ? (
      <div
        ref={tooltipContentRef}
        className={`tooltip ${isAbove ? "tooltip-above" : "tooltip-below"}`}
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <h4
            className="tooltip-title"
            style={{ display: "block", visibility: "visible" }}
          >
            {title}
          </h4>
        )}
        <p>{content}</p>
      </div>
    ) : null;

  return (
    <>
      <span
        ref={(el) => {
          tooltipRef.current = el;
          iconRef.current = el; // Captures layout coordinates for text children too!
        }}
        className={`info-icon-container-wrapper ${className}`}
        style={{ display: "inline-flex", cursor: "pointer" }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowTooltip((v) => !v);
        }}
      >
        {children ? (
          <span>{children}</span>
        ) : (
          <img
            src={resolvedIconSrc}
            alt="tooltip"
            className={`info-icon ${iconClassName}`}
          />
        )}
      </span>

      {ReactDOM.createPortal(tooltipContent, portalElement)}
    </>
  );
};

export default Tooltip;
